import { exceptionToString } from '../util/exception';
import type { DynamoItemType} from '../dto/itemTypes';
import { ItemType } from '../dto/itemTypes';
import type { ObjType } from './mos-interfaces';
import xmldom from '@xmldom/xmldom';
import type { Logger } from '../types/logger';

export const getObjType = (itemType: DynamoItemType): ObjType | null => {
  switch (itemType) {
    case ItemType.VIDEO: return 'VIDEO';
    case ItemType.IMAGE: return 'STILL';
    case ItemType.AUDIO: return 'AUDIO';
    default: return null;
  }
};

function getDirectChild(parent: Node, tag: string): Node | null {
  for (let i = 0; i < parent.childNodes.length; i++) {
    const child = parent.childNodes.item(i);
    if (child.nodeName === tag) {
      return child;
    }
  }

  return null;
}

export function getChild(parent: Node, ...tags: string[]): Node | null {
  for (const tag of tags) {
    const child = getDirectChild(parent, tag);
    if (child === null) {
      return null;
    }
    parent = child;
  }

  return parent;
}

export function getChildTextContent(parent: Node, ...tags: string[]): string | null {
  const node = getChild(parent, ...tags);
  if (!node) return null;
  return node.textContent;
}

export function getDirectChildren(parent: Node, tag: string): Node[] {
  const children = [];
  for (let i = 0; i < parent.childNodes.length; i++) {
    const child = parent.childNodes.item(i);
    if (child.nodeName === tag) {
      children.push(child);
    }
  }

  return children;
}

export function getValueOrNullFromTag(parent: Node, ...tags: string[]) {
  const child = getChild(parent, ...tags);
  return child?.textContent ?? null;
}

export function getValueOrNullFromAttribute(node: Node, attributeName: string): string | null {
  // attributes is not part of the regular Node definition but it is exposed
  // in nodes obtained via the xmldom parser
  const attributes: NamedNodeMap | undefined = (node as any).attributes;
  if (!attributes) {
    return null;
  }

  for (let i = 0; i < attributes.length; i++) {
    const attr = attributes.item(i);
    if (attr?.name === attributeName) {
      return attr?.value;
    }
  }
  return null;
}

/**
 * MOS integers are supposed to be either decimal or hexadecimal
 * if the prefix 0x or 0X is present.
 */
export function parseMosInteger(s: string): number {
  if (s.startsWith('0x') || s.startsWith('0X')) {
    return parseInt(s, 16);
  }
  return parseInt(s, 10);
}

/**
 * The MOS protocol expects dates to be formatted like this:
 *
 * "Time: Time object changed status. Format is YYYY-MM-DD'T'hh:mm:ss[,ddd]['Z'], e.g. 2009-04-11T14:22:07,125Z
 * or 2009-04-11T14:22:07,125-05:00.  Parameters displayed within brackets are optional. [,ddd] represents
 * fractional time in which all three digits must be present. ['Z'] indicates time zone which can be expressed
 * as an offset from UTC in hours and minutes.  Optionally, the time zone may be replaced by the character 'Z'
 * to indicate UTC.""
 *
 * This is the same as the format used by Date.toISOString() except that it uses a comma instead of a point
 * before milliseconds.
 */
export function formatMosDate(date: Date) {
  return date.toISOString().replace('.', ',');
}

/**
 * See formatMosDate for the expected format of date strings in MOS.
 * This function will also handle a Date.toISOString formatted input.
 */
export function parseMosDate(logger: Logger, mosDate: string): Date {
  try {
    const parsed = new Date(mosDate.replace(',', '.'));
    if (isNaN(parsed.getTime())) {
      throw new Error(`"${mosDate}" is not a valid MOS date`);
    }
    return parsed;
  } catch (e) {
    logger.warn(`Could not parse MOS date '${mosDate}' ${exceptionToString(e)}`);
    throw new Error(`"${mosDate}" is not a valid MOS date`);
  }
}


const supportedMosMessages = [
  'heartbeat', 'reqMachInfo', 'mosObjCreate', 'mosReqObj', 'roMetadataReplace', 'roElementAction', 'roCreate', 'roDelete', 'roList'
] as const;

export type SupportedMosMessage = typeof supportedMosMessages[number];

export function getMosMsgType(mosRoot: Node): SupportedMosMessage | null {
  const rootChildrenNames = new Set<string>();
  for (let i = 0; i < mosRoot.childNodes.length; i++) {
    rootChildrenNames.add(mosRoot.childNodes.item(i).nodeName);
  }
  return supportedMosMessages.find(msgType => rootChildrenNames.has(msgType)) ?? null;
}


export function parseMosXmlString(logger: Logger, mosXml: string): Node | null {
  let mosRoot: Node | null = null;
  try {
    const doc = new xmldom.DOMParser({
      errorHandler: (level: string, msg: any) => {
        throw new Error(`${level}: ${msg}`);
      }
    }).parseFromString(mosXml.trim(), 'text/xml');
    const isValidXml = doc.getElementsByTagName('parsererror').length === 0;
    if (!isValidXml) {
      logger.warn('Invalid MOS message, errors when parsing xml');
      return null;
    }
    mosRoot = doc.firstChild;
    if (!mosRoot || mosRoot.nodeName !== 'mos') {
      logger.warn('Invalid MOS message, root node is not mos node');
      return null;
    }
    return mosRoot;
  } catch (err) {
    logger.warn(`Failed parsing MOS xml: ${exceptionToString(err)}`);
    return null;
  }
}
